import React from 'react';
import { IActiveTournamentRoom, ITournamentRanking, ITournamentState } from '../Core/State/ILobbyTournamentState';
import { t } from '../i18n';

/**
 * Format currency value to USD format
 */
export const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(amount);
};

/**
 * Format currency value to USD format.
 * Omits decimals for whole numbers, shows up to 2 decimal places otherwise.
 */
export const formatCurrencySmartDecimal = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    }).format(amount);
};

/**
 * Format a number as a string without currency symbols
 * Returns '0' for null/undefined values
 * Returns integer values without decimals, or non-integers with 2 decimal places
 */
export const formatNumber = (num: number | undefined | null) => {
    if (num === undefined || num === null) return '0';
    return Number.isInteger(num) ? num.toString() : num.toFixed(2);
};

/**
 * Formats a number into a comma-separated string (e.g. 1000 -> 1,000).
 * Returns '0' if the value is undefined or null.
 */
export const formatChips = (value: number | undefined | null): string => {
    if (value === undefined || value === null) return '0';
    return new Intl.NumberFormat('en-US').format(value);
};

/**
 * Format time difference between two dates
 */
export const formatTimeDifference = (targetTime: Date, currentTime: Date = new Date()): string => {
    const diffMs = targetTime.getTime() - currentTime.getTime();

    if (diffMs <= 0) return t('Tournament:status.ended');

    const diffSec = Math.floor(diffMs / 1000);
    const days = Math.floor(diffSec / 86400);
    const hours = Math.floor((diffSec % 86400) / 3600);
    const minutes = Math.floor((diffSec % 3600) / 60);
    const seconds = diffSec % 60;

    if (days > 0) {
        return `${days}d ${hours}h`;
    } else if (hours > 0) {
        return `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
        return `${minutes}m ${seconds}s`;
    } else {
        return `${seconds}s`;
    }
};

/**
 * Check if late registration is currently open
 * Fails closed on server-provided closed states, then falls back to client-side computation.
 */
export const isLateRegistrationOpen = (t: ITournamentState, now: Date = new Date()): boolean => {
    if (!t) return false;

    if (t.lateRegistrationClosedAt != null) return false;
    if (t.tournamentExtraInformation?.isFinalTable === true) return false;
    if (t.tournamentExtraInformation?.finalTableFeaturesDisabled === true) return false;
    if (t.tournamentExtraInformation?.lateRegistrationStatus === 'expired') return false;

    if (typeof t.isLateRegistrationActive === 'boolean') {
        return t.isLateRegistrationActive;
    }

    // Fallback to client-side computation
    const isRunning = t.status === 'running';
    const hasLateRegWindow = typeof t.lateRegistrationMinutes === 'number' && t.lateRegistrationMinutes > 0;
    if (!isRunning || !hasLateRegWindow) return false;

    const startMs = new Date(t.startTime).getTime();
    const lateRegEnd = startMs + (t.lateRegistrationMinutes as number) * 60_000;
    const lateRegOpen = now.getTime() < lateRegEnd;
    const capacityOpen = (t.registeredPlayerCount ?? t.playerList?.length ?? 0) < t.maxPlayers;
    return lateRegOpen && capacityOpen;
};

/**
 * Check if tournament registration should be offered.
 * Capacity and entry mode are evaluated by callers so they can show the right disabled state.
 */
export const canRegisterForTournament = (t: ITournamentState, now: Date = new Date()): boolean => {
    if (!t) return false;

    if (t.lateRegistrationClosedAt != null) return false;
    if (t.tournamentExtraInformation?.isFinalTable === true) return false;
    if (t.tournamentExtraInformation?.finalTableFeaturesDisabled === true) return false;
    if (t.status === 'completed' || t.status === 'cancelled' || t.status === 'paused') return false;
    if (t.status === 'running') return isLateRegistrationOpen(t, now);

    if (typeof t.isRegistrationOpen === 'boolean') {
        return t.isRegistrationOpen;
    }

    return t.status === 'registration' || t.status === 'scheduled' || t.status === 'late_registration' || isLateRegistrationOpen(t, now);
};

export const isTournamentRegisteredByUser = (
    tournament: ITournamentState | null | undefined,
    userId: string | undefined
): boolean => {
    if (!tournament || !userId) return false;
    if (tournament.isUserRegistered === true) return true;

    const playerList = Array.isArray(tournament.playerList) ? tournament.playerList : [];
    const registeredPlayers = Array.isArray(tournament.registeredPlayers) ? tournament.registeredPlayers : [];
    return (
        playerList.some((player) => player.userId === userId) ||
        registeredPlayers.some((player) => player.userId === userId)
    );
};

/**
 * Get remaining time in milliseconds for late registration
 */
export const getLateRegistrationRemaining = (t: ITournamentState, now: Date = new Date()): number => {
    if (!t || typeof t.lateRegistrationMinutes !== 'number') return 0;
    const startMs = new Date(t.startTime).getTime();
    const lateRegEnd = startMs + t.lateRegistrationMinutes * 60_000;
    return Math.max(0, lateRegEnd - now.getTime());
};

/**
 * Get effective prize pool (returns max of current and guaranteed prize)
 */
export const getEffectivePrize = (tournament: ITournamentState): number => {
    const currentPrize = tournament.prizePool || 0;
    const guaranteedPrize = tournament.guaranteedPrizePool || 0;

    // If guaranteedPrizePool exists, return the maximum of current prize and guaranteed prize
    if (tournament.guaranteedPrizePool !== undefined && tournament.guaranteedPrizePool > 0) {
        return Math.max(currentPrize, guaranteedPrize);
    }

    // Otherwise, just use the current prize
    return currentPrize;
};

export const getTournamentTotalEntrants = (tournament: ITournamentState): number => {
    return tournament.totalRegisteredPlayerCount ?? tournament.registeredPlayerCount ?? tournament.playerCount ?? 0;
};

export const getTournamentRemainingPlayers = (tournament: ITournamentState): number => {
    if (tournament.status === 'completed' || tournament.status === 'cancelled') return 0;
    return tournament.playersRemaining ?? tournament.remainingPlayerCount ?? 0;
};

const hasTournamentRemainingPlayerCount = (tournament: ITournamentState): boolean => {
    return typeof tournament.playersRemaining === 'number' || typeof tournament.remainingPlayerCount === 'number';
};

export const getTournamentRoomActivePlayerCount = (room: IActiveTournamentRoom): number => {
    return room.activePlayerCount ?? room.playerCount;
};

export const shouldShowTournamentRemainingCount = (tournament: ITournamentState): boolean => {
    return (tournament.status === 'running' && hasTournamentRemainingPlayerCount(tournament)) ||
        (tournament.status === 'paused' && hasTournamentRemainingPlayerCount(tournament)) ||
        tournament.status === 'completed' ||
        tournament.status === 'cancelled';
};

export const isTerminalTournamentStatus = (status: ITournamentState['status']): boolean => {
    return status === 'completed' || status === 'cancelled';
};

/**
 * Calculate registration window times
 */
export const getRegistrationTimes = (tournament: ITournamentState) => {
    if (!tournament) return { registrationStart: null as Date | null, registrationEnd: null as Date | null, lateRegistrationEnd: null as Date | null };

    const startTime = new Date(tournament.startTime);

    // Registration start time
    const registrationStart = new Date(startTime);
    if (tournament.registrationDurationMinutes) {
        registrationStart.setMinutes(registrationStart.getMinutes() - tournament.registrationDurationMinutes);
    }

    // Late registration end time
    const lateRegistrationEnd = new Date(startTime);
    if (tournament.lateRegistrationMinutes) {
        lateRegistrationEnd.setMinutes(lateRegistrationEnd.getMinutes() + tournament.lateRegistrationMinutes);
    } else {
        // If no late registration, registration ends at tournament start
        return { registrationStart, registrationEnd: startTime, lateRegistrationEnd: null };
    }

    return { registrationStart, registrationEnd: startTime, lateRegistrationEnd };
};

/**
 * Get tournament sort priority for sorting tournaments
 * Priority: Late Registration (0) < Running (1) < Starting Soon (2) < Upcoming (3) < Finished (4)
 */
export const getTournamentSortPriority = (t: ITournamentState, now: Date): number => {
    const start = new Date(t.startTime);
    const msUntilStart = start.getTime() - now.getTime();
    if (t.status === 'late_registration') return 0;
    if (isLateRegistrationOpen(t, now)) return 0;
    if (t.status === 'running' || t.status === 'paused') return 1;
    if (t.status === 'starting') return 2;
    if (msUntilStart > 0 && msUntilStart <= 60 * 60 * 1000) return 2;
    if (t.status === 'registration' || t.status === 'registering' || t.status === 'scheduled') return 3;
    if (msUntilStart > 60 * 60 * 1000) return 3;
    if (t.status === 'completed' || t.status === 'cancelled') return 4;
    return 3;
};

/**
 * Get ticket eligibility for a tournament
 */
export const getEligibleTickets = (userTickets: any[], tournament: ITournamentState | null): any[] => {
    if (!tournament || !Array.isArray(userTickets)) return [];

    const toIdString = (value: any) => (value !== undefined && value !== null ? String(value) : null);
    const candidateIds = new Set(
        [
            toIdString((tournament as any)?.templateId),
            toIdString((tournament as any)?.tournamentTemplateId),
            toIdString((tournament as any)?.tournamentId),
            toIdString(tournament.gameId)
        ].filter(Boolean)
    );
    const candidateNames = new Set(
        [
            tournament.templateName,
            tournament.name
        ].filter(Boolean)
    );

    return userTickets.filter((ticket: any) => {
        if (!ticket || ticket.used || ticket.expired) return false;

        // Preferred: allowedTournaments metadata
        if (Array.isArray(ticket.allowedTournaments)) {
            const match = ticket.allowedTournaments.some((allowed: any) => {
                if (!allowed) return false;
                const idCandidates = [
                    toIdString(allowed.templateId),
                    toIdString(allowed.templateID),
                    toIdString(allowed.tournamentTemplateId),
                    toIdString(allowed.gameId),
                    toIdString(allowed.tournamentId)
                ].filter(Boolean);
                if (idCandidates.some(id => candidateIds.has(id!))) return true;
                if (allowed.name && candidateNames.has(allowed.name)) return true;
                return false;
            });
            if (match) return true;
        }

        // Legacy fallback: direct tournamentId field on ticket
        const ticketTournamentId = toIdString(ticket.tournamentId);
        if (ticketTournamentId && candidateIds.has(ticketTournamentId)) {
            return true;
        }

        return false;
    });
};

/**
 * Get the color code associated with a tournament status
 * @param status - The tournament status string (e.g., 'running', 'registration', 'late_registration', etc.)
 * @returns The hex color code for the status, or '#FFFFFF' (white) as default if status is not recognized
 */
export const getStateColor = (status: string): string => {
    const colorMap: Record<string, string> = {
        'running': '#6BD2FF',
        'registration': '#77a4ff',
        'late_registration': '#F93751',
        'scheduled': '#77a4ff',
        'starting': '#1BE958',
        'completed': '#afafaf',
        'cancelled': '#afafaf',
        'paused': '#FF9800',
    };

    return colorMap[status] || '#FFFFFF'; // Default color
};

export const getStartText = (tournament: ITournamentState): React.ReactElement => {
    const date = typeof tournament.startTime === 'string' ? new Date(tournament.startTime) : tournament.startTime;
    const now = new Date();
    const timeDiffMs = date.getTime() - now.getTime();

    // Computed late registration window
    if (isLateRegistrationOpen(tournament, now)) {
        const remainingMs = getLateRegistrationRemaining(tournament, now);
        const totalSeconds = Math.floor(remainingMs / 1000);
        const minutes = Math.floor(totalSeconds / 60).toString().padStart(2, '0');
        const seconds = (totalSeconds % 60).toString().padStart(2, '0');
        return React.createElement('span', null,
            `${t('Tournament:startText.lateReg')} `,
            React.createElement('span', { style: { color: '#F93751' } }, `${minutes}:${seconds}`)
        );
    }

    if (tournament.status == 'completed' || tournament.status == 'cancelled' || tournament.status == 'running' || tournament.status === 'late_registration') {
        const statusTranslations: Record<string, string> = {
            'completed': t('Tournament:status.completed'),
            'cancelled': t('Tournament:status.cancelled'),
            'running': t('Tournament:status.running'),
            'late_registration': t('Tournament:status.lateRegistration')
        };
        return React.createElement('span', { style: { color: getStateColor(tournament.status) } }, statusTranslations[tournament.status] || tournament.status);
    }

    if (tournament.status == 'paused') {
        return React.createElement('span', { style: { color: getStateColor(tournament.status) } }, t('Tournament:status.onBreak'));
    }

    // Within 60 minutes
    if (timeDiffMs > 0 && timeDiffMs <= 60 * 60 * 1000) {
        const totalSeconds = Math.floor(timeDiffMs / 1000);
        const minutes = Math.floor(totalSeconds / 60).toString().padStart(2, '0');
        const seconds = (totalSeconds % 60).toString().padStart(2, '0');
        return React.createElement('span', null,
            `${t('Tournament:startText.startsIn')} `,
            React.createElement('span', { style: { color: '#FFC100' } }, `${minutes}:${seconds}`)
        );
    }

    // Check if the date is today
    const isToday =
        date.getDate() === now.getDate() &&
        date.getMonth() === now.getMonth() &&
        date.getFullYear() === now.getFullYear();

    const hours = date.getHours().toString().padStart(2, '0');
    const minutesStr = date.getMinutes().toString().padStart(2, '0');

    if (isToday) {
        return React.createElement('span', null,
            `${t('Tournament:startText.starts')} `,
            React.createElement('span', { style: { color: '#1BE958' } }, `${t('Tournament:startText.at')} ${hours}:${minutesStr}`)
        );
    }

    const day = date.getDate();
    const month = date.toLocaleString(undefined, { month: 'short' });

    return React.createElement('span', null,
        `${t('Tournament:startText.starts')} `,
        React.createElement('span', { style: { color: '#ffffff' } }, `${day} ${month}, ${hours}:${minutesStr}`)
    );
}

/**
 * Get start timer text by remaining time.
 * Under 1 minute => seconds (e.g. "56s"), otherwise => hours and minutes (e.g. "01:12").
 */
export const getStartTimer = (tournament: ITournamentState): string => {
    const date = typeof tournament.startTime === 'string' ? new Date(tournament.startTime) : tournament.startTime;
    const now = new Date();
    let safeDiffMs = Math.max(0, date.getTime() - now.getTime());

    if (safeDiffMs <= 0 && isLateRegistrationOpen(tournament, now)) {
        safeDiffMs = getLateRegistrationRemaining(tournament, now);
    }

    if (safeDiffMs < 60_000) {
        const seconds = Math.ceil(safeDiffMs / 1_000);
        return `${seconds}s`;
    }

    const totalMinutes = Math.floor(safeDiffMs / 60_000);
    const hours = Math.floor(totalMinutes / 60).toString().padStart(2, '0');
    const minutes = (totalMinutes % 60).toString().padStart(2, '0');

    return `${hours}:${minutes}`;
}

/**
 * Get start countdown percentage on a 1-hour scale.
 * 60 minutes remaining => 100, 0 minutes remaining => 0.
 */
export const getStartPercentage = (tournament: ITournamentState): number => {
    const date = typeof tournament.startTime === 'string' ? new Date(tournament.startTime) : tournament.startTime;
    const now = new Date();
    let fullMs = 60 * 60 * 1000;
    let timeDiffMs = date.getTime() - now.getTime();

    if (timeDiffMs <= 0) {
        if (isLateRegistrationOpen(tournament)) {
            timeDiffMs = getLateRegistrationRemaining(tournament);
            if (tournament.lateRegistrationMinutes) {
                fullMs = tournament.lateRegistrationMinutes * 60 * 1000;
            }
        }
    }

    const clampedMs = Math.max(0, Math.min(fullMs, timeDiffMs));
    return Math.round((clampedMs / fullMs) * 100);
};

/**
 * Get start timer text color by remaining time threshold.
 * In the past => red, under 5 minutes => amber, under 1 hour => green, over 1 hour => white.
 */
export const getStartTextColour = (tournament: ITournamentState): string => {
    const date = typeof tournament.startTime === 'string' ? new Date(tournament.startTime) : tournament.startTime;
    const now = new Date();
    const timeDiffMs = date.getTime() - now.getTime();

    if (timeDiffMs <= 0) {
        if (isLateRegistrationOpen(tournament)) {
            return '#F01452';
        }
        return '#527DFF';
    }
    if (timeDiffMs <= 5 * 60_000) return '#FFC100';
    if (timeDiffMs <= 60 * 60_000) return '#20E759';
    return '#FFFFFF';
};

export const getStartTextOnly = (tournament: ITournamentState): string => {
    const date = typeof tournament.startTime === 'string' ? new Date(tournament.startTime) : tournament.startTime;
    const now = new Date();
    const timeDiffMs = date.getTime() - now.getTime();

    if (timeDiffMs <= 0) {
        if (isLateRegistrationOpen(tournament)) {
            return `${t('Tournament:startText.lateReg')}`;
        }
        if (tournament.status === 'completed' || tournament.status === 'cancelled') {
            return `${t('Tournament:status.finished')}`
        }
        return `${t('Tournament:status.running')}`;
    }

    return `${t('Tournament:startText.startsIn')}`;
};

/**
 * Get a clean human-readable string for time remaining until tournament start.
 * Under 5 minutes => seconds (e.g. "123s"), under 1 hour => minutes (e.g. "12min"), over 1 hour => hours and minutes (e.g. "1h 12m").
 */
export const getStartTextClean = (tournament: ITournamentState): string => {
    const date = typeof tournament.startTime === 'string' ? new Date(tournament.startTime) : tournament.startTime;
    const now = new Date();
    let timeDiffMs = Math.max(0, date.getTime() - now.getTime());

    if (timeDiffMs <= 0) {
        if (isLateRegistrationOpen(tournament)) {
            timeDiffMs = getLateRegistrationRemaining(tournament);
        } else {
            timeDiffMs = now.getTime() - date.getTime();
        }
    }

    if (timeDiffMs <= 60_000) {
        const seconds = Math.ceil(timeDiffMs / 1_000);
        return `${seconds}s`;
    }

    if (timeDiffMs <= 60 * 60_000) {
        const minutes = Math.floor(timeDiffMs / 60_000);
        return `${minutes}min`;
    }

    const hours = Math.floor(timeDiffMs / 3_600_000);
    const minutes = Math.floor((timeDiffMs % 3_600_000) / 60_000);
    return `${hours}h ${minutes}m`;
};

/**
 * Returns the number of players who have been awarded a prize.
 */
export const getPrizeCount = (tournament: ITournamentState): string => {
    if (!tournament.rankings?.length) return '0';
    return `${tournament.rankings.filter(r => r.prize != null && r.prize > 0).length} ${t('Tournament:info.players')}`;
};
